
import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Loader2, MessagesSquare, Pencil, Search, Filter, Trash2, ChevronLeft, ChevronRight, CheckSquare, Square, Grid3X3, List, MoreHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/contexts/AuthContext";

interface Chatbot {
  id: string;
  name: string;
  description: string | null;
  active: boolean;
  created_at: string;
  user_id: string;
  avatar_url: string | null;
}

export function ChatbotList() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isCreating, setIsCreating] = useState(false);
  const { user } = useAuth(); // Use centralized auth state
  const userId = user?.id || null;

  // Enhanced state for search, filter, and pagination
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("all");
  const [sortBy, setSortBy] = useState<"name" | "created_at" | "status">("created_at");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState<"tile" | "table">("tile");
  
  // Dynamic items per page based on view mode
  const itemsPerPage = viewMode === "tile" ? 9 : 10;
  
  // Bulk selection state
  const [selectedChatbots, setSelectedChatbots] = useState<Set<string>>(new Set());
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);

  const { data: chatbots, isLoading, error, refetch } = useQuery({
    queryKey: ["chatbots", userId],
    queryFn: async () => {
      if (!userId) throw new Error("Not authenticated");

      console.log("Fetching chatbots for user:", userId);
      const { data, error } = await supabase
        .from("chatbots")
        .select("*")
        .eq("user_id", userId)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching chatbots:", error);
        throw error;
      }

      console.log("Fetched chatbots:", data);
      return data as Chatbot[];
    },
    enabled: !!userId, // Only run query when we have a userId
  });

  // Delete mutation
  const deleteChatbot = useMutation({
    mutationFn: async (chatbotId: string) => {
      const { error } = await supabase
        .from("chatbots")
        .delete()
        .eq("id", chatbotId)
        .eq("user_id", userId); // Ensure user can only delete their own chatbots
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chatbots", userId] });
      toast({
        title: "Success",
        description: "Chatbot deleted successfully",
      });
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to delete chatbot",
      });
    },
  });

  // Bulk delete mutation
  const bulkDeleteChatbots = useMutation({
    mutationFn: async (chatbotIds: string[]) => {
      const { error } = await supabase
        .from("chatbots")
        .delete()
        .in("id", chatbotIds)
        .eq("user_id", userId); // Ensure user can only delete their own chatbots
      
      if (error) throw error;
    },
    onSuccess: (_, chatbotIds) => {
      queryClient.invalidateQueries({ queryKey: ["chatbots", userId] });
      setSelectedChatbots(new Set());
      setBulkDeleteDialogOpen(false);
      toast({
        title: "Success",
        description: `${chatbotIds.length} chatbot${chatbotIds.length === 1 ? '' : 's'} deleted successfully`,
      });
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to delete chatbots",
      });
    },
  });

  // Filter and search logic
  const filteredAndSortedChatbots = useMemo(() => {
    if (!chatbots) return [];
    
    const filtered = chatbots.filter((chatbot) => {
      // Search filter
      const matchesSearch = searchTerm === "" || 
        chatbot.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (chatbot.description && chatbot.description.toLowerCase().includes(searchTerm.toLowerCase()));
      
      // Status filter
      const matchesStatus = statusFilter === "all" || 
        (statusFilter === "active" && chatbot.active) ||
        (statusFilter === "inactive" && !chatbot.active);
      
      return matchesSearch && matchesStatus;
    });
    
    // Sort
    filtered.sort((a, b) => {
      let aValue: string | number | boolean;
      let bValue: string | number | boolean;
      
      switch (sortBy) {
        case "name":
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case "status":
          aValue = a.active;
          bValue = b.active;
          break;
        case "created_at":
        default:
          aValue = new Date(a.created_at).getTime();
          bValue = new Date(b.created_at).getTime();
          break;
      }
      
      if (sortOrder === "asc") {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
    
    return filtered;
  }, [chatbots, searchTerm, statusFilter, sortBy, sortOrder]);

  // Pagination logic
  const totalPages = Math.ceil(filteredAndSortedChatbots.length / itemsPerPage);
  const paginatedChatbots = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredAndSortedChatbots.slice(startIndex, endIndex);
  }, [filteredAndSortedChatbots, currentPage, itemsPerPage]);

  // Reset pagination when filters change
  useMemo(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, sortBy, sortOrder]);

  // Bulk selection helpers
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedChatbots(new Set(paginatedChatbots.map(chatbot => chatbot.id)));
    } else {
      setSelectedChatbots(new Set());
    }
  };

  const handleSelectChatbot = (chatbotId: string, checked: boolean) => {
    const newSelected = new Set(selectedChatbots);
    if (checked) {
      newSelected.add(chatbotId);
    } else {
      newSelected.delete(chatbotId);
    }
    setSelectedChatbots(newSelected);
  };

  const isAllSelected = paginatedChatbots.length > 0 && paginatedChatbots.every(chatbot => selectedChatbots.has(chatbot.id));
  const isIndeterminate = selectedChatbots.size > 0 && !isAllSelected;

  const handleBulkDelete = () => {
    if (selectedChatbots.size === 0) return;
    setBulkDeleteDialogOpen(true);
  };

  const confirmBulkDelete = () => {
    bulkDeleteChatbots.mutate(Array.from(selectedChatbots));
  };

  const handleCreateChatbot = async () => {
    if (!userId) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "You must be logged in to create a chatbot",
      });
      return;
    }

    setIsCreating(true);
    try {
      const { data, error } = await supabase
        .from("chatbots")
        .insert([
          {
            name: "New Chatbot",
            description: "A new chatbot",
            user_id: userId,
            welcome_message: "Welcome! How can I help you today?",
            completion_message: "Thank you for your responses!"
          },
        ])
        .select()
        .single();

      if (error) throw error;

      // Create a default theme for the chatbot using the correct schema
      const { error: themeError } = await supabase.from("themes").insert([
        {
          chatbot_id: data.id,
          name: "Default Theme",
          primarycolor: "#2563EB",
          backgroundcolor: "#FFFFFF",
          userbubblecolor: "#3B82F6",
          botbubblecolor: "#F3F4F6",
          usertextcolor: "#FFFFFF",
          bottextcolor: "#1F2937",
          headercolor: "#3B82F6",
          headertextcolor: "#FFFFFF",
          borderradius: "8px",
          fontfamily: "Inter",
          buttoncolor: "#3B82F6",
          buttontextcolor: "#FFFFFF",
          position: "right",
          buttontext: "Chat with us",
          welcomebubblecolor: "#3B82F6",
          welcomebubbletextcolor: "#FFFFFF",
          inputbordercolor: "#E5E7EB",
          inputborderradius: "8px",
          inputbackgroundcolor: "#FFFFFF",
          avatarbackgroundcolor: "#3B82F6",
          headerbackgroundcolor: "#3B82F6",
          showavatar: true,
          showtitle: true,
          showclosebutton: true,
          showreset: true,
          showsources: true,
          showtimestamp: true,
          showtypingindicator: true,
          showuseravatar: false
        },
      ]);

      if (themeError) throw themeError;

      // Refetch the chatbots list to show the new chatbot
      await refetch();

      navigate(`/portal/builder/${data.id}`);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    } finally {
      setIsCreating(false);
    }
  };

  if (isLoading || !userId) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <p className="text-red-500">Failed to load chatbots</p>
        <Button variant="outline" onClick={() => refetch()} className="mt-4">
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">My Chatbots</h2>
        <Button onClick={handleCreateChatbot} disabled={isCreating}>
          <Plus className="mr-2 h-4 w-4" />
          {isCreating ? "Creating..." : "Create Chatbot"}
        </Button>
      </div>

      {/* Search and Filter Controls */}
      <div className="flex flex-col sm:flex-row gap-4 items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            placeholder="Search chatbots by name or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <div className="flex gap-2">
          <Select value={statusFilter} onValueChange={(value: "all" | "active" | "inactive") => setStatusFilter(value)}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
          
          <Select value={`${sortBy}-${sortOrder}`} onValueChange={(value) => {
            const [field, order] = value.split("-");
            setSortBy(field as "name" | "created_at" | "status");
            setSortOrder(order as "asc" | "desc");
          }}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="created_at-desc">Newest First</SelectItem>
              <SelectItem value="created_at-asc">Oldest First</SelectItem>
              <SelectItem value="name-asc">Name A-Z</SelectItem>
              <SelectItem value="name-desc">Name Z-A</SelectItem>
              <SelectItem value="status-desc">Active First</SelectItem>
              <SelectItem value="status-asc">Inactive First</SelectItem>
            </SelectContent>
          </Select>
          
          {/* View Mode Toggle */}
          <div className="flex border rounded-lg overflow-hidden">
            <Button
              variant={viewMode === "tile" ? "default" : "outline"}
              size="sm"
              onClick={() => setViewMode("tile")}
              className="rounded-none border-0"
            >
              <Grid3X3 className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === "table" ? "default" : "outline"}
              size="sm"
              onClick={() => setViewMode("table")}
              className="rounded-none border-0 border-l"
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Bulk Actions Toolbar */}
      {selectedChatbots.size > 0 && (
        <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-blue-900">
              {selectedChatbots.size} chatbot{selectedChatbots.size === 1 ? '' : 's'} selected
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedChatbots(new Set())}
              className="text-blue-600 hover:text-blue-700"
            >
              Clear selection
            </Button>
          </div>
          <Button
            variant="destructive"
            size="sm"
            onClick={handleBulkDelete}
            className="gap-2"
          >
            <Trash2 className="h-4 w-4" />
            Delete Selected
          </Button>
        </div>
      )}

      {/* Results Summary */}
      {chatbots && (
        <div className="flex justify-between items-center text-sm text-gray-600">
          <span>
            Showing {paginatedChatbots.length} of {filteredAndSortedChatbots.length} chatbots
            {filteredAndSortedChatbots.length !== chatbots.length && ` (${chatbots.length} total)`}
          </span>
          {totalPages > 1 && (
            <span>Page {currentPage} of {totalPages}</span>
          )}
        </div>
      )}

      {!chatbots || chatbots.length === 0 ? (
        <Card className="p-8 text-center">
          <MessagesSquare className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-4 text-lg font-medium">No chatbots yet</h3>
          <p className="mt-2 text-sm text-gray-500">
            Create your first chatbot to get started
          </p>
          <Button onClick={handleCreateChatbot} className="mt-4" disabled={isCreating}>
            <Plus className="mr-2 h-4 w-4" />
            Create Chatbot
          </Button>
        </Card>
      ) : filteredAndSortedChatbots.length === 0 ? (
        <Card className="p-8 text-center">
          <Filter className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-4 text-lg font-medium">No chatbots found</h3>
          <p className="mt-2 text-sm text-gray-500">
            Try adjusting your search terms or filters
          </p>
          <Button 
            variant="outline" 
            onClick={() => {
              setSearchTerm("");
              setStatusFilter("all");
              setSortBy("created_at");
              setSortOrder("desc");
            }} 
            className="mt-4"
          >
            Clear Filters
          </Button>
        </Card>
      ) : (
        <>
          {/* Select All Checkbox */}
          {paginatedChatbots.length > 0 && (
            <div className="flex items-center gap-2 mb-4">
              <Checkbox
                checked={isAllSelected}
                ref={(el) => {
                  if (el) {
                    const input = el.querySelector('input');
                    if (input) input.indeterminate = isIndeterminate;
                  }
                }}
                onCheckedChange={handleSelectAll}
                aria-label="Select all chatbots"
              />
              <label className="text-sm font-medium">
                Select all {paginatedChatbots.length} chatbots on this page
              </label>
            </div>
          )}

          {/* Tile View */}
          {viewMode === "tile" && (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {paginatedChatbots.map((chatbot) => (
                <Card
                  key={chatbot.id}
                  className={`p-6 hover:shadow-lg transition-shadow relative ${
                    selectedChatbots.has(chatbot.id) ? 'ring-2 ring-blue-500 bg-blue-50' : ''
                  }`}
                >
                  {/* Selection Checkbox */}
                  <div className="absolute top-4 right-4">
                    <Checkbox
                      checked={selectedChatbots.has(chatbot.id)}
                      onCheckedChange={(checked) => handleSelectChatbot(chatbot.id, checked as boolean)}
                      aria-label={`Select ${chatbot.name}`}
                    />
                  </div>
                  
                  <div className="flex items-start gap-4 pr-8">
                    <Avatar className="h-12 w-12">
                      {chatbot.avatar_url ? (
                        <AvatarImage src={chatbot.avatar_url} alt={chatbot.name} />
                      ) : (
                        <AvatarFallback>
                          {chatbot.name.substring(0, 2).toUpperCase()}
                        </AvatarFallback>
                      )}
                    </Avatar>
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold">{chatbot.name}</h3>
                            <Badge variant={chatbot.active ? "default" : "secondary"}>
                              {chatbot.active ? "Active" : "Inactive"}
                            </Badge>
                          </div>
                          {chatbot.description && (
                            <p className="text-sm text-gray-500 mt-1">
                              {chatbot.description}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="mt-4 flex items-center justify-between">
                        <span className="text-xs text-gray-500">
                          Created{" "}
                          {new Date(chatbot.created_at).toLocaleDateString(undefined, {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          })}
                        </span>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-2"
                            onClick={() => navigate(`/portal/builder/${chatbot.id}`)}
                          >
                            <Pencil className="h-4 w-4" />
                            Edit
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className="gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Chatbot</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to delete "{chatbot.name}"? This action cannot be undone.
                                  All associated data including conversations, themes, and settings will be permanently removed.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => deleteChatbot.mutate(chatbot.id)}
                                  className="bg-red-600 hover:bg-red-700"
                                  disabled={deleteChatbot.isPending}
                                >
                                  {deleteChatbot.isPending ? "Deleting..." : "Delete"}
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}

          {/* Table View */}
          {viewMode === "table" && (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox
                        checked={isAllSelected}
                        ref={(el) => {
                          if (el) {
                            const input = el.querySelector('input');
                            if (input) input.indeterminate = isIndeterminate;
                          }
                        }}
                        onCheckedChange={handleSelectAll}
                        aria-label="Select all chatbots"
                      />
                    </TableHead>
                    <TableHead>Chatbot</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="w-24">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedChatbots.map((chatbot) => (
                    <TableRow 
                      key={chatbot.id} 
                      className={selectedChatbots.has(chatbot.id) ? 'bg-blue-50' : ''}
                    >
                      <TableCell>
                        <Checkbox
                          checked={selectedChatbots.has(chatbot.id)}
                          onCheckedChange={(checked) => handleSelectChatbot(chatbot.id, checked as boolean)}
                          aria-label={`Select ${chatbot.name}`}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8">
                            {chatbot.avatar_url ? (
                              <AvatarImage src={chatbot.avatar_url} alt={chatbot.name} />
                            ) : (
                              <AvatarFallback className="text-xs">
                                {chatbot.name.substring(0, 2).toUpperCase()}
                              </AvatarFallback>
                            )}
                          </Avatar>
                          <div>
                            <div className="font-medium">{chatbot.name}</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={chatbot.active ? "default" : "secondary"}>
                          {chatbot.active ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-gray-500">
                          {chatbot.description || "No description"}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-gray-500">
                          {new Date(chatbot.created_at).toLocaleDateString(undefined, {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })}
                        </span>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/portal/builder/${chatbot.id}`)}>
                              <Pencil className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <DropdownMenuItem
                                  onSelect={(e) => e.preventDefault()}
                                  className="text-red-600 focus:text-red-600"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete Chatbot</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Are you sure you want to delete "{chatbot.name}"? This action cannot be undone.
                                    All associated data including conversations, themes, and settings will be permanently removed.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => deleteChatbot.mutate(chatbot.id)}
                                    className="bg-red-600 hover:bg-red-700"
                                    disabled={deleteChatbot.isPending}
                                  >
                                    {deleteChatbot.isPending ? "Deleting..." : "Delete"}
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-2 mt-6">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              
              <div className="flex gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                  // Show first page, last page, current page, and pages around current
                  if (
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 1 && page <= currentPage + 1)
                  ) {
                    return (
                      <Button
                        key={page}
                        variant={currentPage === page ? "default" : "outline"}
                        size="sm"
                        onClick={() => setCurrentPage(page)}
                        className="w-8 h-8 p-0"
                      >
                        {page}
                      </Button>
                    );
                  } else if (
                    page === currentPage - 2 ||
                    page === currentPage + 2
                  ) {
                    return (
                      <span key={page} className="px-2 py-1 text-gray-400">
                        ...
                      </span>
                    );
                  }
                  return null;
                })}
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </>
      )}

      {/* Bulk Delete Confirmation Dialog */}
      <AlertDialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Multiple Chatbots</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedChatbots.size} chatbot{selectedChatbots.size === 1 ? '' : 's'}? This action cannot be undone.
              All associated data including conversations, themes, and settings will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmBulkDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={bulkDeleteChatbots.isPending}
            >
              {bulkDeleteChatbots.isPending ? "Deleting..." : `Delete ${selectedChatbots.size} Chatbot${selectedChatbots.size === 1 ? '' : 's'}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
